import { showToast } from "./toast.js";
import { globalState } from "../inference/globals.js";
import { ApiService } from "../inference/api.js";
import { collectPostprocessingParameters } from "../inference/postprocessing.js";

/**
 * Feedback form management class
 */
export class FeedbackManager {
    constructor() {
        this.isVisible = false;
        this.currentRating = 0;
        this.currentProcessingMode = null;
        this.feedbackData = null;
        this.feedbackConfig = null;
        this.initialized = false;
    }

    /**
     * Initialize the feedback manager
     */
    async init() {
        if (this.initialized) {
            return;
        }

        try {
            await this.loadFeedbackConfig();
        } catch (error) {
            console.warn(
                "Failed to load feedback config, using defaults:",
                error
            );
        }

        this.setupEventListeners();
        this.loadDoctorName();
        this.setupStarRating();
        this.initialized = true;

        console.log("📝 Feedback manager initialized successfully");
    }

    /**
     * Load feedback configuration from server
     */
    async loadFeedbackConfig() {
        try {
            console.log("📝 Loading feedback configuration from server...");
            this.feedbackConfig = await ApiService.getFeedbackConfig();
            console.log(
                "📝 Feedback configuration loaded:",
                this.feedbackConfig
            );
        } catch (error) {
            console.error("📝 Failed to load feedback configuration:", error);
            // Fallback to default configuration
            this.feedbackConfig = this.getDefaultConfig();
        }
        this.applyGtInfoJsonVisibility();
    }

    /**
     * Show or hide the GT InfoJSON upload block based on server config.
     */
    applyGtInfoJsonVisibility() {
        const group = document.getElementById("feedback-gt-info-json-group");
        if (!group) return;
        const enabled =
            this.feedbackConfig?.gt_info_json_upload?.enabled !== false;
        group.style.display = enabled ? "" : "none";
    }

    /**
     * Get default feedback configuration as fallback
     */
    getDefaultConfig() {
        return {
            questions: {
                arrangement: [
                    {
                        id: "alignment_standards",
                        type: "rating_with_comment",
                        question:
                            "Rate the alignment result of the upper and lower jaw for clinical standards",
                        required: true
                    },
                    {
                        id: "occlusal_relationship",
                        type: "rating_with_comment",
                        question:
                            "Rate the occlusal relationship between the upper and lower jaw for clinical standards",
                        required: true
                    },
                    {
                        id: "treatment_expectations",
                        type: "rating_with_comment",
                        question:
                            "Rate how well the result meets your expectation for the post-treatment effects of the patient",
                        required: true
                    }
                ],
                teethml: [
                    {
                        id: "segmentation_accuracy",
                        type: "rating_with_comment",
                        question:
                            "Rate the teeth segmentation accuracy for clinical use",
                        required: true
                    }
                ],
                defectml: [
                    {
                        id: "defect_detection",
                        type: "rating_with_comment",
                        question:
                            "Rate the clinical relevance of the detected defects",
                        required: true
                    }
                ],
                keypointml: [
                    {
                        id: "keypoint_accuracy",
                        type: "rating_with_comment",
                        question:
                            "Rate the anatomical correctness of the detected keypoints",
                        required: true
                    }
                ]
            },
            rating_descriptions: {
                0: "",
                1: "1 - Unacceptable",
                2: "2 - Below Expected",
                3: "3 - Average",
                4: "4 - Good",
                5: "5 - Very Good"
            },
            form_settings: {
                doctor_name_required: true,
                rating_required: true,
                comments_required: false
            }
        };
    }

    /**
     * Setup all event listeners
     */
    setupEventListeners() {
        // Submit button
        const submitButton = document.getElementById("feedback-submit");
        if (submitButton) {
            submitButton.addEventListener("click", () => this.handleSubmit());
        }

        // Feedback buttons (both external and sidebar)
        const feedbackButton = document.getElementById("feedback-button");
        if (feedbackButton) {
            feedbackButton.addEventListener("click", () => this.toggle());
        }

        const sidebarFeedbackButton = document.getElementById(
            "sidebar-feedback-button"
        );
        if (sidebarFeedbackButton) {
            sidebarFeedbackButton.addEventListener("click", () =>
                this.toggle()
            );
        }

        // Collapsed feedback button in sidebar header
        const collapsedFeedbackButton = document.getElementById(
            "feedback-button-collapsed"
        );
        if (collapsedFeedbackButton) {
            collapsedFeedbackButton.addEventListener("click", () =>
                this.toggle()
            );
        }

        // Doctor name input - save to localStorage on change
        const doctorNameInput = document.getElementById("doctor-name");
        if (doctorNameInput) {
            doctorNameInput.addEventListener("blur", () => {
                this.saveDoctorName(doctorNameInput.value);
            });
        }

        // GT InfoJSON file input: show selected filename
        const gtInfoJsonFile = document.getElementById(
            "feedback-gt-info-json-file"
        );
        const gtInfoJsonFileName = document.getElementById(
            "feedback-gt-info-json-file-name"
        );
        if (gtInfoJsonFile && gtInfoJsonFileName) {
            gtInfoJsonFile.addEventListener("change", (event) => {
                const file = event.target.files[0];
                if (file) {
                    gtInfoJsonFileName.textContent = file.name;
                } else {
                    gtInfoJsonFileName.textContent = "";
                }
            });
        }

        // Close on ESC key
        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape" && this.isVisible) {
                this.hide();
            }
        });

        // Prevent closing when clicking inside the sidebar
        const sidebar = document.getElementById("feedback-sidebar");
        if (sidebar) {
            sidebar.addEventListener("click", (event) => {
                event.stopPropagation();
            });
        }
    }

    /**
     * Setup star rating functionality
     */
    setupStarRating() {
        const stars = document.querySelectorAll(".star");
        const ratingDescription = document.getElementById("rating-description");

        // Get descriptions from config or use defaults
        const descriptions = this.feedbackConfig?.rating_descriptions || {
            0: "",
            1: "1 - Unacceptable",
            2: "2 - Below Expected",
            3: "3 - Average",
            4: "4 - Good",
            5: "5 - Very Good"
        };

        stars.forEach((star, index) => {
            star.addEventListener("click", () => {
                this.currentRating = index + 1;
                this.updateStarDisplay();
                if (ratingDescription) {
                    ratingDescription.textContent =
                        descriptions[this.currentRating];
                }
            });

            star.addEventListener("mouseenter", () => {
                const rating = index + 1;
                this.highlightStars(rating);
                if (ratingDescription) {
                    ratingDescription.textContent = descriptions[rating];
                }
            });
        });

        const starsContainer = document.getElementById("rating-stars");
        if (starsContainer) {
            starsContainer.addEventListener("mouseleave", () => {
                this.updateStarDisplay();
                if (ratingDescription) {
                    ratingDescription.textContent =
                        descriptions[this.currentRating];
                }
            });
        }
    }

    /**
     * Highlight stars up to the given rating
     * @param {number} rating - Rating from 1-5
     */
    highlightStars(rating) {
        const stars = document.querySelectorAll(".star");
        stars.forEach((star, index) => {
            if (index < rating) {
                star.classList.add("active");
            } else {
                star.classList.remove("active");
            }
        });
    }

    /**
     * Update star display based on current rating
     */
    updateStarDisplay() {
        this.highlightStars(this.currentRating);
    }

    /**
     * Load doctor name from localStorage
     */
    loadDoctorName() {
        try {
            const savedName = localStorage.getItem("teethml-doctor-name");
            const doctorNameInput = document.getElementById("doctor-name");
            if (savedName && doctorNameInput) {
                doctorNameInput.value = savedName;
            }
        } catch (error) {
            console.warn(
                "Failed to load doctor name from localStorage:",
                error
            );
        }
    }

    /**
     * Save doctor name to localStorage
     * @param {string} name - Doctor name to save
     */
    saveDoctorName(name) {
        try {
            if (name && name.trim()) {
                localStorage.setItem("teethml-doctor-name", name.trim());
            }
        } catch (error) {
            console.warn("Failed to save doctor name to localStorage:", error);
        }
    }

    /**
     * Toggle the feedback form
     */
    toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * Show the feedback form
     * @param {string} processingMode - Current processing mode
     * @param {Object} predictionData - Prediction results data
     */
    show(processingMode = null, predictionData = null) {
        console.log("📝 Showing feedback form for mode:", processingMode);

        // Use stored processing mode if not provided
        if (!processingMode && this.currentProcessingMode) {
            processingMode = this.currentProcessingMode;
        }

        if (processingMode) {
            this.currentProcessingMode = processingMode;

            // Preserve existing predictionData if new predictionData is not provided
            const existingPredictionData = this.feedbackData?.predictionData;
            const finalPredictionData =
                predictionData !== null
                    ? predictionData
                    : existingPredictionData;

            const fileData = this.collectFileData();
            this.feedbackData = {
                processingMode,
                predictionData: finalPredictionData,
                timestamp: new Date().toISOString(),
                // Original processing job id, used by the server to link this
                // feedback to the job's audit record.
                originalItemId: globalState.lastProcessedItemId || null,
                files: fileData,
                // Create meshFiles array from the collected file data for compatibility with feedback display
                meshFiles: this.extractMeshFilesFromFileData(fileData)
            };

            // Show/hide mode-specific questions
            this.updateQuestionVisibility(processingMode);
        }

        // Reset form
        this.resetForm();

        // Show the feedback form in the right sidebar
        const feedbackFormSection = document.getElementById(
            "feedback-form-section"
        );
        const rightSidebar = document.getElementById("right-sidebar");
        const appContainer = document.querySelector(".app-container");

        if (feedbackFormSection) {
            feedbackFormSection.style.display = "block";
            this.isVisible = true;
        }

        // In Processing mode, temporarily show the right sidebar for feedback
        if (rightSidebar && appContainer) {
            const isProcessingMode = !globalState.feedbackMode;
            if (isProcessingMode) {
                rightSidebar.style.display = "block";
                appContainer.classList.remove("hide-right-sidebar");

                // Expand right sidebar if it's collapsed
                if (
                    rightSidebar.classList.contains("right-sidebar-collapsed")
                ) {
                    rightSidebar.classList.remove("right-sidebar-collapsed");
                    appContainer.classList.remove("right-collapsed");
                    const collapseBtn =
                        document.getElementById("collapse-right");
                    if (collapseBtn) {
                        collapseBtn.innerHTML = "❯";
                        collapseBtn.title = "Collapse sidebar";
                    }
                    rightSidebar.querySelector(".sidebar-title").textContent =
                        "Feedback";
                }
            }
        }

        // Focus on first input
        setTimeout(() => {
            const doctorNameInput = document.getElementById("doctor-name");
            if (doctorNameInput && !doctorNameInput.value) {
                doctorNameInput.focus();
            }
        }, 300);
    }

    /**
     * Hide the feedback form
     */
    hide() {
        console.log("📝 Hiding feedback form");

        // Hide the feedback form in the right sidebar
        const feedbackFormSection = document.getElementById(
            "feedback-form-section"
        );
        if (feedbackFormSection) {
            feedbackFormSection.style.display = "none";
            this.isVisible = false;
        }

        // In Processing mode, hide the right sidebar again after feedback
        const rightSidebar = document.getElementById("right-sidebar");
        const appContainer = document.querySelector(".app-container");
        if (rightSidebar && appContainer) {
            const isProcessingMode = !globalState.feedbackMode;
            if (isProcessingMode) {
                rightSidebar.style.display = "none";
                appContainer.classList.add("hide-right-sidebar");

                // Reset sidebar title
                const sidebarTitle =
                    rightSidebar.querySelector(".sidebar-title");
                if (sidebarTitle) {
                    sidebarTitle.textContent = "Details";
                }
            }
        }

        // Reset form after animation
        setTimeout(() => {
            this.resetForm();
        }, 300);
    }

    /**
     * Update question visibility based on processing mode
     * @param {string} processingMode - Current processing mode
     */
    updateQuestionVisibility(processingMode) {
        const questionsContainer = document.getElementById("dynamic-questions");

        if (!questionsContainer || !this.feedbackConfig) {
            return;
        }

        // Clear existing questions
        questionsContainer.innerHTML = "";

        // Get questions for this processing mode
        const questions = this.feedbackConfig.questions[processingMode] || [];

        if (questions.length === 0) {
            questionsContainer.style.display = "none";
            return;
        }

        // Generate HTML for each question
        questions.forEach((question, index) => {
            if (question.type === "yes_no") {
                const questionDiv = this.createYesNoQuestion(question, index);
                questionsContainer.appendChild(questionDiv);
            } else if (
                question.type === "rating_with_comment" ||
                question.type === "rating_and_comment"
            ) {
                const questionDiv = this.createRatingAndCommentQuestion(
                    question,
                    index
                );
                questionsContainer.appendChild(questionDiv);
            } else {
                console.warn(
                    `📝 Unknown question type: ${question.type}, treating as yes_no`
                );
                const questionDiv = this.createYesNoQuestion(question, index);
                questionsContainer.appendChild(questionDiv);
            }
        });

        questionsContainer.style.display = "block";
        console.log(
            `📝 Generated ${questions.length} questions for ${processingMode} mode`
        );
    }

    /**
     * Create a yes/no question element
     * @param {Object} question - Question configuration
     * @param {number} index - Question index
     * @returns {HTMLElement} Question element
     */
    createYesNoQuestion(question, index) {
        const questionDiv = document.createElement("div");
        questionDiv.className = "yes-no-question";

        const questionText = document.createElement("div");
        questionText.className = "question-text";
        const requiredMark =
            question.required !== false
                ? ' <span class="required-asterisk">*</span>'
                : "";
        questionText.innerHTML = `<strong>Question ${index + 1}:</strong> ${
            question.question
        }${requiredMark}`;

        const radioContainer = document.createElement("div");
        radioContainer.className = "radio-container";

        // Create radio wrapper
        const radioWrapper = document.createElement("div");
        radioWrapper.className = "radio-wrapper";

        // Create Yes radio button and label
        const yesLabel = document.createElement("label");
        yesLabel.className = "radio-label yes-label";

        const yesRadio = document.createElement("input");
        yesRadio.type = "radio";
        yesRadio.id = `${question.id}-yes`;
        yesRadio.name = question.id;
        yesRadio.value = "yes";
        yesRadio.className = "radio-input";

        const yesText = document.createElement("span");
        yesText.textContent = "Yes";

        yesLabel.appendChild(yesRadio);
        yesLabel.appendChild(yesText);

        // Create No radio button and label
        const noLabel = document.createElement("label");
        noLabel.className = "radio-label no-label";

        const noRadio = document.createElement("input");
        noRadio.type = "radio";
        noRadio.id = `${question.id}-no`;
        noRadio.name = question.id;
        noRadio.value = "no";
        noRadio.className = "radio-input";

        const noText = document.createElement("span");
        noText.textContent = "No";

        noLabel.appendChild(noRadio);
        noLabel.appendChild(noText);

        // Add event listeners to handle visual feedback
        yesRadio.addEventListener("change", (e) => {
            if (e.target.checked) {
                radioWrapper.classList.add("yes-selected");
                radioWrapper.classList.remove("no-selected");
            }
        });

        noRadio.addEventListener("change", (e) => {
            if (e.target.checked) {
                radioWrapper.classList.add("no-selected");
                radioWrapper.classList.remove("yes-selected");
            }
        });

        // Start with no selection (empty by default)
        radioWrapper.appendChild(yesLabel);
        radioWrapper.appendChild(noLabel);

        radioContainer.appendChild(radioWrapper);
        questionDiv.appendChild(questionText);
        questionDiv.appendChild(radioContainer);

        return questionDiv;
    }

    /**
     * Create a rating and comment question element
     * @param {Object} question - Question configuration
     * @param {number} index - Question index
     * @returns {HTMLElement} Question element
     */
    createRatingAndCommentQuestion(question, index) {
        const questionDiv = document.createElement("div");
        questionDiv.className = "rating-comment-question";

        const questionText = document.createElement("div");
        questionText.className = "question-text";
        const requiredMark =
            question.required !== false
                ? ' <span class="required-asterisk">*</span>'
                : "";
        questionText.innerHTML = `<strong>Question ${index + 1}:</strong> ${
            question.question
        }${requiredMark}`;

        // Rating section
        const ratingSection = document.createElement("div");
        ratingSection.className = "rating-section";

        const ratingLabel = document.createElement("div");
        ratingLabel.className = "rating-label";
        ratingLabel.textContent = "Rating:";

        const ratingContainer = document.createElement("div");
        ratingContainer.className = "star-rating";
        ratingContainer.dataset.questionId = question.id; // Add question ID to container

        for (let i = 1; i <= 5; i++) {
            const star = document.createElement("span");
            star.className = "star";
            star.textContent = "★";
            star.dataset.rating = i;
            star.dataset.questionId = question.id;

            star.addEventListener("click", (e) => {
                const rating = parseInt(e.target.dataset.rating);
                const questionId = e.target.dataset.questionId;

                console.log(
                    `📝 Star clicked - Rating: ${rating}, Question: ${questionId}`
                );

                // Update visual state
                this.updateQuestionStarDisplay(ratingContainer, rating);

                // Store rating on the container
                ratingContainer.dataset.rating = rating;

                console.log(
                    `📝 Rating stored on container for ${questionId}: ${rating}`,
                    {
                        container: ratingContainer,
                        storedRating: ratingContainer.dataset.rating
                    }
                );
            });

            // Add hover events for individual question stars
            star.addEventListener("mouseenter", (e) => {
                const rating = parseInt(e.target.dataset.rating);
                this.updateQuestionStarDisplay(ratingContainer, rating);
            });

            ratingContainer.appendChild(star);
        }

        // Add mouseleave event to the container to restore actual rating
        ratingContainer.addEventListener("mouseleave", () => {
            const actualRating = parseInt(ratingContainer.dataset.rating) || 0;
            this.updateQuestionStarDisplay(ratingContainer, actualRating);
        });

        // Comment section
        const commentSection = document.createElement("div");
        commentSection.className = "comment-section";

        const commentLabel = document.createElement("label");
        commentLabel.textContent = "Comments:";
        commentLabel.setAttribute("for", `comment-${question.id}`);

        const commentTextarea = document.createElement("textarea");
        commentTextarea.id = `comment-${question.id}`;
        commentTextarea.name = `comment-${question.id}`;
        commentTextarea.className = "comment-input";
        commentTextarea.placeholder =
            "Optional comments about this question...";
        commentTextarea.rows = 3;

        // Assemble the question
        ratingSection.appendChild(ratingLabel);
        ratingSection.appendChild(ratingContainer);

        commentSection.appendChild(commentLabel);
        commentSection.appendChild(commentTextarea);

        questionDiv.appendChild(questionText);
        questionDiv.appendChild(ratingSection);
        questionDiv.appendChild(commentSection);

        return questionDiv;
    }

    /**
     * Update star display for individual question ratings
     * @param {HTMLElement} ratingContainer - Container with stars
     * @param {number} rating - Rating from 1-5
     */
    updateQuestionStarDisplay(ratingContainer, rating) {
        const allStars = ratingContainer.querySelectorAll(".star");
        allStars.forEach((s, idx) => {
            if (idx < rating) {
                s.classList.add("active");
            } else {
                s.classList.remove("active");
            }
        });
    }

    /**
     * Initialize feedback data after processing completes and show feedback button
     * @param {string} processingMode - Current processing mode
     * @param {Object} predictionData - Prediction results data
     */
    initializeForProcessing(processingMode, predictionData = null) {
        console.log(
            "📝 Initializing feedback for processing mode:",
            processingMode
        );

        this.currentProcessingMode = processingMode;
        const fileData = this.collectFileData();

        this.feedbackData = {
            processingMode,
            predictionData,
            timestamp: new Date().toISOString(),
            // Original processing job id, used by the server to link this
            // feedback to the job's audit record.
            originalItemId: globalState.lastProcessedItemId || null,
            files: fileData,
            // Create meshFiles array from the collected file data for compatibility with feedback display
            meshFiles: this.extractMeshFilesFromFileData(fileData)
        };

        // Show the feedback button
        this.showFeedbackButton();
    }

    /**
     * Show the feedback buttons
     */
    showFeedbackButton() {
        const feedbackButton = document.getElementById("feedback-button");
        const sidebarFeedbackButton = document.getElementById(
            "sidebar-feedback-button"
        );

        if (feedbackButton) {
            feedbackButton.style.display = "flex";
        }

        if (sidebarFeedbackButton) {
            sidebarFeedbackButton.style.display = "flex";
        }

        console.log("📝 Feedback buttons shown");
    }

    /**
     * Hide the feedback buttons
     */
    hideFeedbackButton() {
        const feedbackButton = document.getElementById("feedback-button");
        const sidebarFeedbackButton = document.getElementById(
            "sidebar-feedback-button"
        );

        if (feedbackButton) {
            feedbackButton.style.display = "none";
        }

        if (sidebarFeedbackButton) {
            sidebarFeedbackButton.style.display = "none";
        }

        console.log("📝 Feedback buttons hidden");
    }

    /**
     * Reset the form to initial state
     */
    resetForm() {
        // Reset rating
        this.currentRating = 0;
        this.updateStarDisplay();

        const ratingDescription = document.getElementById("rating-description");
        if (ratingDescription) {
            ratingDescription.textContent = "";
        }

        // Reset radio buttons in dynamic questions to empty state
        const radioInputs = document.querySelectorAll(
            "#dynamic-questions .radio-input"
        );
        radioInputs.forEach((radioInput) => {
            radioInput.checked = false;

            // Reset visual state to empty
            const wrapper = radioInput.closest(".radio-wrapper");
            if (wrapper) {
                wrapper.classList.remove("no-selected");
                wrapper.classList.remove("yes-selected");
            }
        });

        // Reset comment
        const commentsTextarea = document.getElementById("additional-comments");
        if (commentsTextarea) {
            commentsTextarea.value = "";
        }

        // Reset GT InfoJSON inputs
        const gtFileInput = document.getElementById(
            "feedback-gt-info-json-file"
        );
        if (gtFileInput) gtFileInput.value = "";
        const gtFileName = document.getElementById(
            "feedback-gt-info-json-file-name"
        );
        if (gtFileName) gtFileName.textContent = "";
        const gtAzurePath = document.getElementById(
            "feedback-gt-info-json-azure-path"
        );
        if (gtAzurePath) gtAzurePath.value = "";

        // Reset submit button
        const submitButton = document.getElementById("feedback-submit");
        if (submitButton) {
            submitButton.disabled = false;
            submitButton.innerHTML = "Submit Feedback";
        }

        // Show form, hide success
        const formContainer = document.getElementById(
            "feedback-form-container"
        );
        const successContainer = document.getElementById("feedback-success");
        if (formContainer) formContainer.style.display = "block";
        if (successContainer) successContainer.style.display = "none";
    }

    /**
     * Collect current file data
     * @returns {Object} File data object
     */
    collectFileData() {
        const files = {};

        // Get processing mode
        const processingMode =
            document.getElementById("processing-mode-dropdown")?.value ||
            "arrangement";

        if (processingMode === "arrangement") {
            // Collect arrangement files
            const lowerJawFile =
                document.getElementById("lower-jaw-file")?.files[0];
            const upperJawFile =
                document.getElementById("upper-jaw-file")?.files[0];

            if (lowerJawFile) {
                files.lowerJawFile = {
                    name: lowerJawFile.name,
                    size: lowerJawFile.size,
                    type: lowerJawFile.type,
                    file: lowerJawFile // Store the actual file object
                };
            }

            if (upperJawFile) {
                files.upperJawFile = {
                    name: upperJawFile.name,
                    size: upperJawFile.size,
                    type: upperJawFile.type,
                    file: upperJawFile // Store the actual file object
                };
            }

            // Check for Azure paths
            const lowerJawAzurePath = document.getElementById(
                "lower-jaw-azure-path"
            )?.value;
            const upperJawAzurePath = document.getElementById(
                "upper-jaw-azure-path"
            )?.value;

            if (lowerJawAzurePath) {
                files.lowerJawAzurePath = lowerJawAzurePath;
            }

            if (upperJawAzurePath) {
                files.upperJawAzurePath = upperJawAzurePath;
            }

            // Collect postprocessing parameters for arrangement mode
            try {
                const postprocessingData = collectPostprocessingParameters();
                files.postprocessing = postprocessingData;
            } catch (error) {
                console.warn(
                    "Failed to collect postprocessing parameters:",
                    error
                );
                // Set default disabled state if collection fails
                files.postprocessing = { enabled: false };
            }
        } else {
            // Collect single file
            const singleFile = document.getElementById("file-select")?.files[0];

            if (singleFile) {
                files.inputFile = {
                    name: singleFile.name,
                    size: singleFile.size,
                    type: singleFile.type,
                    file: singleFile // Store the actual file object
                };
            }

            // Check for Azure path
            const azurePath =
                document.getElementById("azure-path-input")?.value;
            if (azurePath) {
                files.azurePath = azurePath;
            }
        }

        // Optional GT InfoJSON Azure path
        const gtInfoJsonAzurePath = document
            .getElementById("feedback-gt-info-json-azure-path")
            ?.value?.trim();
        if (gtInfoJsonAzurePath) {
            files.gtInfoJsonAzurePath = gtInfoJsonAzurePath;
        }

        return files;
    }

    /**
     * Extract mesh files from collected file data for display compatibility
     * @param {Object} fileData - Collected file data
     * @returns {Array} Array of mesh file objects
     */
    extractMeshFilesFromFileData(fileData) {
        const meshFiles = [];

        // For arrangement mode, extract lower and upper jaw files
        if (fileData.lowerJawFile?.file) {
            meshFiles.push(fileData.lowerJawFile.file);
        }
        if (fileData.upperJawFile?.file) {
            meshFiles.push(fileData.upperJawFile.file);
        }

        // For other modes, extract the single input file
        if (fileData.inputFile?.file) {
            meshFiles.push(fileData.inputFile.file);
        }

        console.log(
            `📁 Extracted ${meshFiles.length} mesh files from file data for feedback display`
        );
        return meshFiles;
    }

    /**
     * Collect form data
     * @returns {Object} Form data object
     */
    collectFormData() {
        const doctorName =
            document.getElementById("doctor-name")?.value?.trim() || "";
        const additionalComments =
            document.getElementById("additional-comments")?.value?.trim() || "";

        const formData = {
            doctorName,
            rating: this.currentRating,
            additionalComments,
            processingMode: this.currentProcessingMode
        };

        // Collect dynamic questions based on current processing mode
        if (this.feedbackConfig && this.currentProcessingMode) {
            const questions =
                this.feedbackConfig.questions[this.currentProcessingMode] || [];
            const questionResponses = {};

            questions.forEach((question) => {
                if (question.type === "yes_no") {
                    const selectedRadio = document.querySelector(
                        `input[name="${question.id}"]:checked`
                    );
                    if (selectedRadio) {
                        questionResponses[question.id] = selectedRadio.value;
                    } else {
                        questionResponses[question.id] = null;
                    }
                } else if (
                    question.type === "rating_with_comment" ||
                    question.type === "rating_and_comment"
                ) {
                    // Collect rating and comment
                    const ratingContainer = document.querySelector(
                        `.star-rating[data-question-id="${question.id}"]`
                    );
                    let rating = null;

                    if (ratingContainer && ratingContainer.dataset.rating) {
                        rating = parseInt(ratingContainer.dataset.rating);
                        console.log(
                            `📝 Found rating for ${question.id}: ${rating}`
                        );
                    } else {
                        console.warn(
                            `📝 No rating found for question ${question.id}`,
                            {
                                ratingContainer,
                                rating: ratingContainer?.dataset?.rating
                            }
                        );
                    }

                    // Collect comment
                    const commentInput = document.getElementById(
                        `comment-${question.id}`
                    );
                    const comment = commentInput
                        ? commentInput.value.trim()
                        : "";

                    const questionResponse = {
                        rating: rating,
                        comment: comment || null
                    };

                    questionResponses[question.id] = questionResponse;

                    console.log(
                        `📝 Collected response for ${question.id}:`,
                        questionResponse
                    );
                } else {
                    console.warn(
                        `📝 Unknown question type: ${question.type} for question ${question.id}`
                    );
                    questionResponses[question.id] = null;
                }
            });

            // Store responses using the processing mode as key
            if (Object.keys(questionResponses).length > 0) {
                formData.dynamicQuestions = questionResponses;
            }
        }

        console.log(`📝 Final form data collected:`, {
            processingMode: this.currentProcessingMode,
            questionCount: Object.keys(formData.dynamicQuestions || {}).length,
            dynamicQuestions: formData.dynamicQuestions,
            doctorName: formData.doctorName,
            rating: formData.rating
        });

        return formData;
    }

    /**
     * Validate form data
     * @param {Object} formData - Form data to validate
     * @returns {Object} Validation result
     */
    validateForm(formData) {
        const errors = [];

        if (!formData.doctorName) {
            errors.push("Doctor name is required");
        }

        if (formData.rating === 0) {
            errors.push("Please provide a rating");
        }

        // Synchronous GT InfoJSON checks (size + filename).
        // Structural JSON parsing happens async in handleSubmit before upload.
        const gtFile = document.getElementById("feedback-gt-info-json-file")
            ?.files?.[0];
        // Limit is published via feedback config from the server.
        // If absent, skip client-side check.
        const maxKb = this.feedbackConfig?.gt_info_json_upload?.max_size_kb;
        if (gtFile) {
            if (!gtFile.name.toLowerCase().endsWith(".json")) {
                errors.push("GT InfoJSON must be a .json file");
            }
            if (maxKb && gtFile.size > maxKb * 1024) {
                errors.push(
                    `GT InfoJSON exceeds ${maxKb} KB (got ${gtFile.size} bytes)`
                );
            }
        }

        // Check if required questions are answered
        if (this.feedbackConfig && this.currentProcessingMode) {
            const modeQuestions =
                this.feedbackConfig.questions[this.currentProcessingMode] || [];
            const answeredQuestions = formData.dynamicQuestions || {};

            // Validate each question based on its required property
            modeQuestions.forEach((question, index) => {
                const response = answeredQuestions[question.id];
                const isRequired = question.required !== false; // Default to required if not specified

                console.log(
                    `📝 Validating question ${index + 1} (${question.id})`,
                    {
                        questionType: question.type,
                        response: response,
                        question: question.question,
                        isRequired: isRequired
                    }
                );

                // Only validate if the question is required
                if (!isRequired) {
                    console.log(
                        `📝 Question ${
                            index + 1
                        } is optional, skipping validation`
                    );
                    return;
                }

                if (question.type === "yes_no") {
                    if (
                        response === null ||
                        response === undefined ||
                        response === ""
                    ) {
                        const errorMsg = `Please answer Question ${
                            index + 1
                        }: ${question.question}`;
                        console.log(`📝 Validation error (yes_no):`, errorMsg);
                        errors.push(errorMsg);
                    }
                } else if (
                    question.type === "rating_with_comment" ||
                    question.type === "rating_and_comment"
                ) {
                    if (
                        !response ||
                        response.rating === null ||
                        response.rating === undefined ||
                        response.rating === 0
                    ) {
                        const errorMsg = `Please provide a rating for Question ${
                            index + 1
                        }: ${question.question}`;
                        console.log(
                            `📝 Validation error (rating_with_comment):`,
                            errorMsg,
                            {
                                hasResponse: !!response,
                                rating: response?.rating,
                                comment: response?.comment
                            }
                        );
                        errors.push(errorMsg);
                    } else {
                        console.log(
                            `📝 Question ${
                                index + 1
                            } validated successfully - rating: ${
                                response.rating
                            }`
                        );
                    }
                } else {
                    // Unknown question type, validate if required
                    if (
                        response === null ||
                        response === undefined ||
                        response === ""
                    ) {
                        const errorMsg = `Please answer Question ${
                            index + 1
                        }: ${question.question}`;
                        console.log(
                            `📝 Validation error (unknown type):`,
                            errorMsg
                        );
                        errors.push(errorMsg);
                    }
                }
            });
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Validate structural shape of GT InfoJSON before submit.
     * Returns null if valid (or no file selected), or an error string.
     */
    async validateGtInfoJsonStructure() {
        const gtFile = document.getElementById("feedback-gt-info-json-file")
            ?.files?.[0];
        if (!gtFile) return null;
        let parsed;
        try {
            parsed = JSON.parse(await gtFile.text());
        } catch (e) {
            return `GT InfoJSON is not valid JSON: ${e.message}`;
        }
        if (
            !parsed ||
            typeof parsed !== "object" ||
            Array.isArray(parsed) ||
            !("Version" in parsed) ||
            !("Objects" in parsed)
        ) {
            return "GT InfoJSON must contain 'Version' and 'Objects'";
        }
        if (!Array.isArray(parsed.Objects)) {
            return "GT InfoJSON 'Objects' must be a list";
        }
        for (let i = 0; i < parsed.Objects.length; i++) {
            const obj = parsed.Objects[i];
            if (!obj || typeof obj !== "object") {
                return `GT InfoJSON Objects[${i}] must be an object`;
            }
            if (!("Type" in obj) || !("Transform" in obj)) {
                return `GT InfoJSON Objects[${i}] must contain 'Type' and 'Transform'`;
            }
            if (!Array.isArray(obj.Transform) || obj.Transform.length !== 16) {
                return `GT InfoJSON Objects[${i}].Transform must be a 16-element list`;
            }
            if (
                !obj.Transform.every(
                    (v) => typeof v === "number" && Number.isFinite(v)
                )
            ) {
                return `GT InfoJSON Objects[${i}].Transform elements must be numbers`;
            }
        }
        return null;
    }

    /**
     * Handle form submission
     */
    async handleSubmit() {
        console.log("📝 Handling feedback form submission");

        // Collect form data
        const formData = this.collectFormData();
        console.log("📝 Collected form data:", formData);

        // Validate form
        const validation = this.validateForm(formData);
        if (!validation.isValid) {
            showToast(validation.errors.join(", "), "exclamation-triangle");
            return;
        }

        // Structural validation of GT InfoJSON
        const gtStructureError = await this.validateGtInfoJsonStructure();
        if (gtStructureError) {
            showToast(gtStructureError, "exclamation-triangle");
            return;
        }

        // Save doctor name
        this.saveDoctorName(formData.doctorName);

        // Update submit button to loading state
        const submitButton = document.getElementById("feedback-submit");
        if (submitButton) {
            submitButton.disabled = true;
            submitButton.innerHTML = `
                <div class="feedback-loading">
                    <div class="feedback-spinner"></div>
                    Submitting...
                </div>
            `;
        }

        try {
            // Prepare submission data
            const submissionData = {
                ...this.feedbackData,
                formData,
                submissionTime: new Date().toISOString()
            };

            console.log("📝 Submitting feedback data:", submissionData);

            // Submit feedback
            await this.submitFeedback(submissionData);

            // Show success state
            this.showSuccessState();
        } catch (error) {
            console.error("📝 Failed to submit feedback:", error);
            showToast(
                "Failed to submit feedback. Please try again.",
                "exclamation-triangle"
            );

            // Reset submit button
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.innerHTML = "Submit Feedback";
            }
        }
    }

    /**
     * Submit feedback to server
     * @param {Object} feedbackData - Complete feedback data
     */
    async submitFeedback(feedbackData) {
        try {
            // Create FormData for file uploads
            const formData = new FormData();

            // Add feedback JSON data
            formData.append("feedback_data", JSON.stringify(feedbackData));

            // Add files if they exist
            await this.appendFiles(formData);

            // Detect GT InfoJSON upload attempt before the response so we can
            // distinguish "no GT submitted" from "GT submitted but server failed
            // to persist it" (server returns gt_info_json_stored=false in that
            // second case after only logging a warning -- without this check the
            // clinician would see a green success toast while their GT was lost).
            const gtInfoJsonFile = document.getElementById(
                "feedback-gt-info-json-file"
            )?.files?.[0];
            const gtInfoJsonAzurePath = document
                .getElementById("feedback-gt-info-json-azure-path")
                ?.value?.trim();
            const gtInfoJsonAttempted = Boolean(
                gtInfoJsonFile || gtInfoJsonAzurePath
            );

            // Submit to server
            const response = await ApiService.submitFeedback(formData);

            if (!response || response.error) {
                throw new Error(response?.error || "Unknown error occurred");
            }

            if (gtInfoJsonAttempted && response.gt_info_json_stored === false) {
                console.warn(
                    "📝 Feedback saved but GT InfoJSON was not stored:",
                    response
                );
                showToast(
                    "Feedback saved, but GT InfoJSON failed to store. Please retry the GT upload.",
                    "exclamation-triangle"
                );
                return;
            }

            console.log("📝 Feedback submitted successfully:", response);
            showToast("Feedback submitted successfully!", "check-circle");
        } catch (error) {
            console.error("📝 Error submitting feedback:", error);
            throw error;
        }
    }

    /**
     * Append files to form data for submission
     * @param {FormData} formData - FormData to append files to
     */
    async appendFiles(formData) {
        const processingMode = this.currentProcessingMode;

        if (processingMode === "arrangement") {
            // Append arrangement files
            const lowerJawFile =
                document.getElementById("lower-jaw-file")?.files[0];
            const upperJawFile =
                document.getElementById("upper-jaw-file")?.files[0];

            if (lowerJawFile) {
                formData.append("lower_jaw_file", lowerJawFile);
            }

            if (upperJawFile) {
                formData.append("upper_jaw_file", upperJawFile);
            }
        } else {
            // Append single file
            const singleFile = document.getElementById("file-select")?.files[0];
            if (singleFile) {
                formData.append("input_file", singleFile);
            }
        }

        // Add prediction results as JSON file if available
        if (this.feedbackData?.predictionData) {
            const predictionBlob = new Blob(
                [JSON.stringify(this.feedbackData.predictionData, null, 2)],
                { type: "application/json" }
            );
            formData.append(
                "prediction_results",
                predictionBlob,
                "prediction_results.json"
            );
        }

        // Optional GT InfoJSON: file takes precedence over Azure path
        const gtInfoJsonFile = document.getElementById(
            "feedback-gt-info-json-file"
        )?.files?.[0];
        const gtInfoJsonAzurePath = document
            .getElementById("feedback-gt-info-json-azure-path")
            ?.value?.trim();
        if (gtInfoJsonFile) {
            formData.append("gt_info_json_file", gtInfoJsonFile);
        } else if (gtInfoJsonAzurePath) {
            formData.append("gt_info_json_azure_path", gtInfoJsonAzurePath);
        }
    }

    /**
     * Show success state
     */
    showSuccessState() {
        const formContainer = document.getElementById(
            "feedback-form-container"
        );
        const successContainer = document.getElementById("feedback-success");

        if (formContainer) {
            formContainer.style.display = "none";
        }

        if (successContainer) {
            successContainer.style.display = "block";
        }

        // Auto-hide after 3 seconds
        setTimeout(() => {
            this.hide();
        }, 3000);
    }
}

// Create global instance
export const feedbackManager = new FeedbackManager();

// Initialize the feedback manager when DOM is ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", async () => {
        try {
            await feedbackManager.init();
        } catch (error) {
            console.warn("Failed to initialize feedback manager:", error);
        }
    });
} else {
    // DOM is already loaded
    (async () => {
        try {
            await feedbackManager.init();
        } catch (error) {
            console.warn("Failed to initialize feedback manager:", error);
        }
    })();
}

// Utility functions for external use
export function showFeedbackForm(processingMode, predictionData = null) {
    feedbackManager.show(processingMode, predictionData);
}

export function hideFeedbackForm() {
    feedbackManager.hide();
}

export function initializeFeedback(processingMode, predictionData = null) {
    feedbackManager.initializeForProcessing(processingMode, predictionData);
}

export function showFeedbackButton() {
    feedbackManager.showFeedbackButton();
}

export function hideFeedbackButton() {
    feedbackManager.hideFeedbackButton();
}
